import { useTheme } from "../ThemeContext";
import { useState, useMemo, useEffect, memo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, AreaChart, Area, PieChart, Pie, Cell } from "recharts";
import { fmt, STATUS, DONE_STATUS, PAY_LABELS, PAY_COLORS, valorReal, calcKpis } from "../theme";
import { KpiCard, DataTable, Card, EmptyState, ChartTooltip } from "../components/UI";
import { exportExcel, exportPDF, fmtMoeda, fmtData, fmtDataCurta, getTurno, filtrarPorPeriodo } from "../utils/exportUtils";

/* ═══════════════════════════════════════════════════
   RELATÓRIOS UNIFICADOS — MixUp Lojista
   Setores: Visão Geral | Vendas | Financeiro |
            Entregadores | Produtos | Cashback
   ═══════════════════════════════════════════════════ */

const PERIODOS = [
  { key: "hoje", label: "Hoje" },
  { key: "ontem", label: "Ontem" },
  { key: "semana", label: "Semana" },
  { key: "mes", label: "Mês" },
  { key: "ano", label: "Ano" },
  { key: "custom", label: "Personalizado" },
];

const TURNOS = [
  { key: "Manha", label: "Manhã (6h-12h)", short: "Manhã", icon: "🌅", color: "#F59E0B" },
  { key: "Tarde", label: "Tarde (12h-18h)", short: "Tarde", icon: "☀️", color: "#3B82F6" },
  { key: "Noite", label: "Noite (18h-6h)", short: "Noite", icon: "🌙", color: "#8B5CF6" },
];

const SETORES = [
  { key: "geral", label: "Visão Geral", icon: "📊", color: "#3B82F6" },
  { key: "vendas", label: "Vendas", icon: "🛒", color: "#22C55E" },
  { key: "financeiro", label: "Financeiro", icon: "💰", color: "#F59E0B" },
  { key: "entregadores", label: "Entregadores", icon: "🏍️", color: "#F97316" },
  { key: "produtos", label: "Produtos", icon: "📦", color: "#06B6D4" },
  { key: "cashback", label: "Cashback", icon: "🪙", color: "#8B5CF6" },
  { key: "taxa_mixup", label: "Taxa MixUp", icon: "💵", color: "#10B981" },
  { key: "cancelamentos", label: "Cancelamentos", icon: "❌", color: "#EF4444" },
];

/* ─── Componentes auxiliares ─── */
const SectionTitle = ({ children, style }) => (
  <h3 style={{ fontSize: 15, fontWeight: 800, marginBottom: 14, fontFamily: "'Plus Jakarta Sans', sans-serif", ...style }}>{children}</h3>
);

const MiniStat = ({ label, value, color, sub }) => {
  const { theme: C } = useTheme();
  return (
    <div style={{ background: C.card, borderRadius: 12, padding: 14, border: `1px solid ${C.border}`, borderLeft: `4px solid ${color || C.textMuted}` }}>
      <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 4, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: color || C.text }}>{value}</div>
      {sub && <div style={{ fontSize: 9, color: C.textMuted, marginTop: 2 }}>{sub}</div>}
    </div>
  );
};

const Pagination = ({ total, perPage, current, onChange, C }) => {
  const totalPag = Math.ceil(total / perPage);
  if (totalPag <= 1) return null;
  return (
    <div style={{ display: "flex", justifyContent: "center", gap: 4, marginTop: 12, flexWrap: "wrap", alignItems: "center" }}>
      {current > 1 && (
        <button onClick={() => onChange(current - 1)} style={{ padding: "6px 10px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: C.textMuted, cursor: "pointer", fontSize: 11, fontWeight: 700 }}>←</button>
      )}
      {Array.from({ length: totalPag }, (_, i) => i + 1)
        .filter(p => p === 1 || p === totalPag || (p >= current - 2 && p <= current + 2))
        .reduce((acc, p, idx, arr) => {
          if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
          acc.push(p);
          return acc;
        }, [])
        .map((p, i) =>
          p === "..." ? <span key={`e${i}`} style={{ color: C.textMuted, fontSize: 11, padding: "0 4px" }}>...</span> : (
            <button key={p} onClick={() => onChange(p)} style={{ width: 30, height: 30, borderRadius: 6, border: `1px solid ${current === p ? C.red : C.border}`, background: current === p ? C.red : "transparent", color: current === p ? "#fff" : C.textMuted, cursor: "pointer", fontSize: 11, fontWeight: 700 }}>{p}</button>
          )
        )}
      {current < totalPag && (
        <button onClick={() => onChange(current + 1)} style={{ padding: "6px 10px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: C.textMuted, cursor: "pointer", fontSize: 11, fontWeight: 700 }}>→</button>
      )}
      <span style={{ fontSize: 10, color: C.textMuted, marginLeft: 8 }}>{current}/{totalPag} ({total})</span>
    </div>
  );
};

const Medal = ({ pos }) => {
  const { theme: C } = useTheme();
  const medals = { 1: "🥇", 2: "🥈", 3: "🥉" };
  return medals[pos] ? <span style={{ fontSize: 16 }}>{medals[pos]}</span> : <span style={{ fontSize: 12, fontWeight: 700, color: C.textMuted }}>{pos}</span>;
};

/* ═══════════════════════════════════════════════════ */
/*              COMPONENTE PRINCIPAL                  */
/* ═══════════════════════════════════════════════════ */
const Relatorios = memo(function Relatorios({ orders, lang, lojaId }) {
  const { theme: C } = useTheme();
  const [setor, setSetor] = useState("geral");
  const [periodo, setPeriodo] = useState("mes");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [pag, setPag] = useState(1);
  const [selectedEnt, setSelectedEnt] = useState(null);
  const [entNomes, setEntNomes] = useState({});

  /* ─── Marcação de taxa paga ─── */
  const [selectedPed, setSelectedPed] = useState({}); // { pedido_id: true }
  const [savingTaxa, setSavingTaxa] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null); // {tipo, ids, total, entNome, entId?}

  const lojistaNome = (() => {
    try {
      const raw = localStorage.getItem("mixup_loja_auth");
      if (raw) { const d = JSON.parse(raw); return d?.dados?.nome || d?.nome || ("Loja #" + lojaId); }
    } catch {}
    return "Loja #" + lojaId;
  })();

  const marcarTaxasPagas = async (ids, paga, obs) => {
    if (!ids || ids.length === 0) return;
    setSavingTaxa(true);
    try {
      const r = await fetch("https://mixupbr.com.br/api/pedidos/?acao=marcar_taxa_paga", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids, paga: paga ? 1 : 0, loja_id: lojaId, pago_por: lojistaNome, obs: obs || null }),
      });
      const d = await r.json();
      if (d.ok) {
        // Atualiza localmente (sem precisar refetch)
        const idsSet = new Set(ids.map(String));
        const novos = orders.map(o => idsSet.has(String(o.id)) ? {
          ...o,
          taxa_paga: paga ? 1 : 0,
          taxa_paga_em: paga ? new Date().toISOString().slice(0,19).replace("T"," ") : null,
          taxa_paga_por: paga ? lojistaNome : null,
          taxa_paga_obs: paga ? (obs || null) : null,
        } : o);
        // Se o componente pai não recarrega, mantém estado interno via mutação direta
        orders.splice(0, orders.length, ...novos);
        setSelectedPed({});
        setConfirmModal(null);
        alert(`✓ ${d.atualizados || ids.length} pedido(s) atualizado(s)`);
      } else {
        alert("Erro: " + (d.error || "desconhecido"));
      }
    } catch (e) {
      alert("Erro de rede: " + e.message);
    }
    setSavingTaxa(false);
  };

  const marcarTodasEntregador = async (entId, entNome, obs) => {
    setSavingTaxa(true);
    try {
      const body = { entregador_id: entId, loja_id: lojaId, pago_por: lojistaNome, obs: obs || null };
      if (periodo !== "ano" && dataInicio) body.periodo_inicio = dataInicio;
      if (periodo !== "ano" && dataFim) body.periodo_fim = dataFim;
      const r = await fetch("https://mixupbr.com.br/api/pedidos/?acao=marcar_taxa_entregador", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (d.ok) {
        // Atualiza localmente todos do entregador no período (com taxa_paga 0)
        const novos = orders.map(o => {
          const matchEnt = String(o.entregador_id) === String(entId);
          const matchPendente = !Number(o.taxa_paga || 0);
          const matchTaxa = Number(o.taxa_entrega || 0) > 0;
          if (matchEnt && matchPendente && matchTaxa) {
            return { ...o, taxa_paga: 1, taxa_paga_em: new Date().toISOString().slice(0,19).replace("T"," "), taxa_paga_por: lojistaNome, taxa_paga_obs: obs || null };
          }
          return o;
        });
        orders.splice(0, orders.length, ...novos);
        setConfirmModal(null);
        alert(`✓ ${d.atualizados || 0} pedido(s) do entregador ${entNome} marcados como pagos`);
      } else {
        alert("Erro: " + (d.error || "desconhecido"));
      }
    } catch (e) {
      alert("Erro de rede: " + e.message);
    }
    setSavingTaxa(false);
  };
  const POR_PAGINA = 25;

  /* Reset página ao mudar setor/período */
  useEffect(() => { setPag(1); }, [setor, periodo, dataInicio, dataFim]);
  useEffect(() => { setSelectedEnt(null); }, [setor]);

  /* Buscar nomes dos entregadores */
  useEffect(() => {
    const fetchNomes = async () => {
      try {
        const r = await fetch("https://mixupbr.com.br/api/loja_entregadores/?loja_id=" + lojaId);
        const d = await r.json();
        if (d.ok && d.dados) {
          const map = {};
          d.dados.forEach(function(e) { if (e.id) map[e.id] = e.nome || ("Entregador #" + e.id); });
          setEntNomes(map);
        }
      } catch (e) { console.error("Erro ao buscar entregadores:", e); }
    };
    if (lojaId) fetchNomes();
  }, [lojaId]);

  /* ─── Dados filtrados ─── */
  const filtrados = useMemo(() => filtrarPorPeriodo(orders, periodo, dataInicio, dataFim), [orders, periodo, dataInicio, dataFim]);

  // Classificação de pedidos:
  // - isAvulsa: só chamada de entregador (NÃO é venda, NÃO gera comissão)
  // - isPDV: venda balcão via PDV (É venda, NÃO gera comissão — paga no ato)
  // - isAppCliente: pedido pelo app do cliente (É venda E gera comissão)
  // - Catálogo: É venda, NÃO gera comissão (igual PDV)
  const isAvulsa = (o) => String(o?.codigo||"").startsWith("AVL") || String(o?.observacao||"").includes("[ENTREGA AVULSA]");
  const isPDV = (o) => o?.canal === "pdv_interno" || String(o?.codigo||"").startsWith("PDV");
  const isAppCliente = (o) => !isAvulsa(o) && !isPDV(o) && (o?.canal === "app" || !o?.canal || o?.canal === "");

  // Vendas reais = entregues + em_entrega MAS EXCLUI entrega avulsa
  const vendidos = useMemo(() => filtrados.filter(o => DONE_STATUS.includes(o.status) && !isAvulsa(o)), [filtrados]);
  const avulsas = useMemo(() => filtrados.filter(isAvulsa), [filtrados]);
  const cancelados = useMemo(() => filtrados.filter(o => o.status === "cancelado"), [filtrados]);

  /* ─── KPIs gerais ─── */
  const totalFat = vendidos.reduce((s, o) => s + valorReal(o), 0);
  const totalSub = vendidos.reduce((s, o) => s + Number(o.subtotal || 0), 0);
  const totalTaxa = vendidos.reduce((s, o) => s + Number(o.taxa_entrega || 0), 0);
  const totalDesc = vendidos.reduce((s, o) => s + Number(o.desconto || 0), 0);
  const ticketMedio = vendidos.length > 0 ? totalFat / vendidos.length : 0;
  const taxaCanc = filtrados.length > 0 ? ((cancelados.length / filtrados.length) * 100).toFixed(1) : "0";
  const cashUsado = vendidos.reduce((s, o) => s + Number(o.cashback_usado || 0), 0);
  const cashGerado = vendidos.reduce((s, o) => s + Number(o.cashback_gerado || 0), 0);

  /* ─── Por turno ─── */
  const turnoData = useMemo(() => {
    const m = { Manha: { ped: 0, fat: 0, canc: 0, taxa: 0 }, Tarde: { ped: 0, fat: 0, canc: 0, taxa: 0 }, Noite: { ped: 0, fat: 0, canc: 0, taxa: 0 } };
    filtrados.forEach(o => {
      const t = getTurno(o.criado_em);
      if (m[t]) {
        m[t].ped++;
        // Só conta faturamento se for venda real (não avulsa)
        if (DONE_STATUS.includes(o.status) && !isAvulsa(o)) { m[t].fat += valorReal(o); }
        // Taxa entrega conta para TODOS os entregues (avulsa, app, catálogo, PDV)
        if (DONE_STATUS.includes(o.status)) { m[t].taxa += Number(o.taxa_entrega || 0); }
        if (o.status === "cancelado") m[t].canc++;
      }
    });
    return m;
  }, [filtrados]);

  /* ─── Por forma pagamento ─── */
  const porPag = useMemo(() => {
    const m = {};
    vendidos.forEach(o => {
      const k = o.forma_pagamento || "outro";
      if (!m[k]) m[k] = { tipo: PAY_LABELS[k] || k, key: k, ped: 0, fat: 0 };
      m[k].ped++;
      m[k].fat += valorReal(o);
    });
    return Object.values(m).sort((a, b) => b.fat - a.fat);
  }, [vendidos]);
  const pgPie = porPag.map(p => ({ name: p.tipo, value: p.fat, color: PAY_COLORS[p.key] || "#888" }));

  /* ─── Por dia ─── */
  const porDia = useMemo(() => {
    const m = {};
    vendidos.forEach(o => {
      const d = o.criado_em?.slice(0, 10);
      if (d) {
        if (!m[d]) m[d] = { ped: 0, fat: 0, canc: 0 };
        m[d].ped++;
        m[d].fat += valorReal(o);
      }
    });
    cancelados.forEach(o => {
      const d = o.criado_em?.slice(0, 10);
      if (d) { if (!m[d]) m[d] = { ped: 0, fat: 0, canc: 0 }; m[d].canc++; }
    });
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b)).map(([d, v]) => ({ dia: d.slice(5), full: d, ...v }));
  }, [vendidos, cancelados]);

  /* ─── Por hora ─── */
  const porHora = useMemo(() => {
    const hMap = {};
    filtrados.forEach(o => { if (o.criado_em) { const h = new Date(o.criado_em).getHours(); hMap[h] = (hMap[h] || 0) + 1; } });
    return Array.from({ length: 18 }, (_, i) => ({ h: `${i + 6}h`, v: hMap[i + 6] || 0 }));
  }, [filtrados]);

  /* ─── Entregadores ─── */
  const porEnt = useMemo(() => {
    const m = {};
    filtrados.filter(o => o.entregador_id).forEach(o => {
      const id = o.entregador_id;
      if (!m[id]) m[id] = { id, nome: entNomes[id] || o.entregador_nome || ("Entregador #" + id), ped: 0, entregas: 0, fat: 0, taxa: 0, taxaPaga: 0, taxaPendente: 0, canc: 0, turno: { Manha: 0, Tarde: 0, Noite: 0 } };
      m[id].ped++;
      if (DONE_STATUS.includes(o.status)) {
        m[id].entregas++;
        m[id].fat += Number(o.total || 0);
        const tx = Number(o.taxa_entrega || 0);
        m[id].taxa += tx;
        if (Number(o.taxa_paga || 0) === 1) m[id].taxaPaga += tx;
        else m[id].taxaPendente += tx;
        const t = getTurno(o.criado_em);
        if (m[id].turno[t] !== undefined) m[id].turno[t]++;
      }
      if (o.status === "cancelado") m[id].canc++;
    });
    return Object.values(m).sort((a, b) => b.entregas - a.entregas);
  }, [filtrados, entNomes]);

  /* ─── Produtos ─── */
  const porProduto = useMemo(() => {
    const m = {};
    vendidos.forEach(o => {
      try {
        const itens = typeof o.itens === "string" ? JSON.parse(o.itens) : (o.itens || []);
        itens.forEach(it => {
          const nome = it.nome || it.produto_nome || "Produto sem nome";
          const key = nome.toLowerCase().trim();
          if (!m[key]) m[key] = { nome, qtd: 0, fat: 0 };
          m[key].qtd += Number(it.quantidade || 1);
          m[key].fat += Number(it.preco || it.valor || 0) * Number(it.quantidade || 1);
        });
      } catch {}
    });
    return Object.values(m).sort((a, b) => b.qtd - a.qtd);
  }, [vendidos]);

  /* ─── Motivos cancelamento ─── */
  const motivosCanc = useMemo(() => {
    const mm = {};
    cancelados.forEach(o => { const m = o.motivo_cancelamento || "Sem motivo informado"; mm[m] = (mm[m] || 0) + 1; });
    return Object.entries(mm).sort(([, a], [, b]) => b - a).map(([m, c]) => ({ motivo: m, qtd: c }));
  }, [cancelados]);

  /* ─── Cashback por pedido ─── */
  const pedidosCashback = useMemo(() => {
    return filtrados.filter(o => Number(o.cashback_usado || 0) > 0 || Number(o.cashback_gerado || 0) > 0)
      .sort((a, b) => (b.criado_em || "").localeCompare(a.criado_em || ""));
  }, [filtrados]);

  /* ─── Status ─── */
  const statusData = useMemo(() => {
    const sm = {};
    filtrados.forEach(o => { sm[o.status] = (sm[o.status] || 0) + 1; });
    return Object.entries(sm).map(([k, v]) => ({ name: STATUS[k]?.label || k, value: v, color: STATUS[k]?.color || C.textMuted }));
  }, [filtrados, C]);

  /* ─── Faturamento por dia (usado no SetorFinanceiro) ─── */
  const fatDia = useMemo(() => {
    const m = {};
    vendidos.forEach(o => { const d = o.criado_em?.slice(0, 10); if (d) m[d] = (m[d] || 0) + valorReal(o); });
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b)).map(([d, v]) => ({ dia: d.slice(5), valor: Math.round(v * 100) / 100 }));
  }, [vendidos]);

  /* ─── Entregas por turno global (usado no SetorEntregadores) ─── */
  const turnoEnt = useMemo(() => {
    const m = { Manha: 0, Tarde: 0, Noite: 0 };
    filtrados.filter(o => o.entregador_id && DONE_STATUS.includes(o.status)).forEach(o => {
      const t = getTurno(o.criado_em);
      if (m[t] !== undefined) m[t]++;
    });
    return m;
  }, [filtrados]);

  /* ─── Cashback por dia (usado no SetorCashback) ─── */
  const cashDia = useMemo(() => {
    const m = {};
    pedidosCashback.forEach(o => {
      const d = o.criado_em?.slice(0, 10);
      if (d) {
        if (!m[d]) m[d] = { usado: 0, gerado: 0 };
        m[d].usado += Number(o.cashback_usado || 0);
        m[d].gerado += Number(o.cashback_gerado || 0);
      }
    });
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b)).map(([d, v]) => ({ dia: d.slice(5), usado: Math.round(v.usado * 100) / 100, gerado: Math.round(v.gerado * 100) / 100 }));
  }, [pedidosCashback]);

  /* ─── Taxa de serviço MixUp (usado no SetorTaxaMixUp) ─── */
  // Mostra só pedidos cuja taxa AINDA NÃO foi repassada pra MixUp (taxa_servico_repassada = 0 ou null)
  const pedidosTaxaMixup = useMemo(() => {
    return filtrados.filter(o =>
      Number(o.taxa_servico || 0) > 0
      && !Number(o.taxa_servico_repassada || 0)
      && o.status !== "cancelado"
    ).sort((a, b) => (b.criado_em || "").localeCompare(a.criado_em || ""));
  }, [filtrados]);
  const taxaMixupTotal = pedidosTaxaMixup.reduce((s, o) => s + Number(o.taxa_servico || 0), 0);
  const taxaMixupDia = useMemo(() => {
    const m = {};
    pedidosTaxaMixup.forEach(o => {
      const d = o.criado_em?.slice(0, 10);
      if (d) m[d] = (m[d] || 0) + Number(o.taxa_servico || 0);
    });
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b)).map(([d, v]) => ({ dia: d.slice(5), valor: Math.round(v * 100) / 100 }));
  }, [pedidosTaxaMixup]);

  /* ─── Totais de entregadores ─── */
  const totalEntregas = porEnt.reduce((s, e) => s + e.entregas, 0);
  const totalFatEnt = porEnt.reduce((s, e) => s + e.fat, 0);
  const totalTaxaEnt = porEnt.reduce((s, e) => s + e.taxa, 0);
  const mediaEnt = porEnt.length > 0 ? totalEntregas / porEnt.length : 0;

  /* ─── Estilos ─── */
  const iS = { padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 12, outline: "none" };

  /* ═══ Barra de filtros de período ═══ */
  const FilterBar = ({ accentColor, onExportExcel, onExportPDF }) => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 16, padding: "10px 14px", background: C.card, borderRadius: 12, border: `1px solid ${C.border}` }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, marginRight: 4 }}>Período:</span>
      {PERIODOS.map(p => (
        <button key={p.key} onClick={() => setPeriodo(p.key)}
          style={{ padding: "5px 12px", borderRadius: 8, border: `1px solid ${periodo === p.key ? (accentColor || C.red) : C.border}`,
            background: periodo === p.key ? (accentColor || C.red) + "18" : "transparent",
            color: periodo === p.key ? (accentColor || C.red) : C.textMuted,
            cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
          {p.label}
        </button>
      ))}
      {periodo === "custom" && (
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} style={iS} />
          <span style={{ color: C.textMuted, fontSize: 11 }}>até</span>
          <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} style={iS} />
        </div>
      )}
      <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
        {onExportExcel && (
          <button onClick={onExportExcel} style={{ padding: "5px 12px", borderRadius: 8, border: `1px solid ${C.green}40`, background: `${C.green}12`, color: C.green, cursor: "pointer", fontSize: 11, fontWeight: 700 }}>Excel</button>
        )}
        {onExportPDF && (
          <button onClick={onExportPDF} style={{ padding: "5px 12px", borderRadius: 8, border: `1px solid ${C.red}40`, background: `${C.red}12`, color: C.red, cursor: "pointer", fontSize: 11, fontWeight: 700 }}>PDF</button>
        )}
      </div>
    </div>
  );

  /* ═══ Bloco de turnos reutilizável ═══ */
  const TurnoGrid = ({ getData }) => (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 16 }}>
      {TURNOS.map(t => {
        const d = getData(t.key);
        return (
          <div key={t.key} style={{ background: C.card, borderRadius: 12, padding: 14, border: `1px solid ${C.border}`, borderLeft: `4px solid ${t.color}` }}>
            <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 4 }}>{t.icon} {t.short}</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
              {d.map((item, i) => (
                <div key={i} style={{ textAlign: i > 0 ? "right" : "left" }}>
                  <div style={{ fontSize: i === 0 ? 20 : 14, fontWeight: i === 0 ? 800 : 700, color: item.color || C.text }}>{item.value}</div>
                  <div style={{ fontSize: 9, color: C.textMuted }}>{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );

  /* ═══ Linha de pedido para tabela ═══ */
  const OrderRow = ({ o, showTaxa, showCashback, showCheckbox }) => {
    const paga = Number(o.taxa_paga || 0) === 1;
    return (
      <tr key={o.id}>
        {showCheckbox && (
          <td style={{ padding: "10px 12px", textAlign: "center" }}>
            {!paga && Number(o.taxa_entrega || 0) > 0 && (
              <input type="checkbox" checked={!!selectedPed[o.id]}
                onChange={() => setSelectedPed(s => { const n = { ...s }; if (n[o.id]) delete n[o.id]; else n[o.id] = true; return n; })}
                style={{ width: 16, height: 16, cursor: "pointer" }} />
            )}
          </td>
        )}
        <td style={{ padding: "10px 12px", fontWeight: 700, fontSize: 12 }}>#{o.codigo}</td>
        <td style={{ padding: "10px 12px" }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: STATUS[o.status]?.color, background: STATUS[o.status]?.bg, padding: "3px 8px", borderRadius: 6 }}>
            {STATUS[o.status]?.label || o.status}
          </span>
        </td>
        <td style={{ padding: "10px 12px", fontSize: 11, color: C.textSoft }}>{PAY_LABELS[o.forma_pagamento] || o.forma_pagamento || "—"}</td>
        <td style={{ padding: "10px 12px", fontWeight: 800, color: C.green }}>{fmt(valorReal(o))}</td>
        {showTaxa && <td style={{ padding: "10px 12px", fontWeight: 700, color: C.orange }}>{fmt(o.taxa_entrega)}</td>}
        {showTaxa && (
          <td style={{ padding: "10px 12px" }}>
            {Number(o.taxa_entrega || 0) === 0 ? (
              <span style={{ fontSize: 10, color: C.textMuted }}>—</span>
            ) : paga ? (
              <div title={`Pago em ${fmtData(o.taxa_paga_em)} por ${o.taxa_paga_por || "?"}`}>
                <span style={{ fontSize: 10, fontWeight: 700, color: "#16A34A", background: "#16A34A18", padding: "3px 8px", borderRadius: 6 }}>
                  ✓ PAGO
                </span>
                <div style={{ fontSize: 9, color: C.textMuted, marginTop: 2 }}>{fmtDataCurta ? fmtDataCurta(o.taxa_paga_em) : (o.taxa_paga_em || "").slice(5,10)}</div>
                {!showCheckbox && (
                  <button onClick={() => { if (window.confirm("Estornar este pagamento?")) marcarTaxasPagas([o.id], false); }}
                    style={{ marginTop: 4, padding: "2px 6px", fontSize: 9, border: `1px solid ${C.border}`, background: "transparent", borderRadius: 4, cursor: "pointer", color: C.textMuted }}>↩ estornar</button>
                )}
              </div>
            ) : (
              <button onClick={() => marcarTaxasPagas([o.id], true)} disabled={savingTaxa}
                style={{ padding: "4px 10px", fontSize: 10, fontWeight: 700, border: "none", background: "#16A34A", color: "#fff", borderRadius: 6, cursor: "pointer", opacity: savingTaxa ? 0.5 : 1 }}>
                💰 Marcar pago
              </button>
            )}
          </td>
        )}
        {showCashback && <td style={{ padding: "10px 12px", fontWeight: 700, color: "#8B5CF6" }}>{Number(o.cashback_usado || 0) > 0 ? fmt(o.cashback_usado) : "—"}</td>}
        <td style={{ padding: "10px 12px", fontSize: 10, color: C.textMuted }}>{getTurno(o.criado_em)}</td>
        <td style={{ padding: "10px 12px", fontSize: 11, color: C.textMuted }}>{fmtData(o.criado_em)}</td>
      </tr>
    );
  };

  /* ════════════════════════════════════════════════
     EXPORTAÇÕES
     ════════════════════════════════════════════════ */
  const exportVendasExcel = () => {
    const headers = ["Código", "Status", "Pagamento", "Subtotal", "Taxa Entrega", "Cashback", "Total", "Cliente", "Turno", "Data"];
    const rows = vendidos.map(o => [
      o.codigo, STATUS[o.status]?.label || o.status, PAY_LABELS[o.forma_pagamento] || o.forma_pagamento,
      Number(o.subtotal || 0).toFixed(2), Number(o.taxa_entrega || 0).toFixed(2),
      Number(o.cashback_usado || 0).toFixed(2), valorReal(o).toFixed(2),
      o.cliente_nome || `#${o.cliente_id}`, getTurno(o.criado_em), fmtData(o.criado_em)
    ]);
    exportExcel(headers, rows, "relatorio_vendas");
  };

  const exportVendasPDF = () => {
    const kpiHTML = `<div class="kpi-row">
      <div class="kpi"><div class="kpi-val green">${fmtMoeda(totalFat)}</div><div class="kpi-label">Faturamento</div></div>
      <div class="kpi"><div class="kpi-val blue">${vendidos.length}</div><div class="kpi-label">Vendidos</div></div>
      <div class="kpi"><div class="kpi-val orange">${fmtMoeda(ticketMedio)}</div><div class="kpi-label">Ticket Médio</div></div>
      <div class="kpi"><div class="kpi-val red">${cancelados.length} (${taxaCanc}%)</div><div class="kpi-label">Cancelados</div></div>
    </div>`;
    const turnoHTML = `<h2>Desempenho por Turno</h2><table>
      <tr><th>Turno</th><th>Pedidos</th><th>Faturamento</th><th>Cancelados</th></tr>
      ${TURNOS.map(t => `<tr><td>${t.icon} ${t.label}</td><td>${turnoData[t.key].ped}</td><td>${fmtMoeda(turnoData[t.key].fat)}</td><td>${turnoData[t.key].canc}</td></tr>`).join("")}
    </table>`;
    const tabelaHTML = `<h2>Todos os Pedidos (${vendidos.length})</h2><table>
      <tr><th>Código</th><th>Status</th><th>Pagamento</th><th>Total</th><th>Cashback</th><th>Turno</th><th>Data</th></tr>
      ${vendidos.map(o => `<tr><td>#${o.codigo}</td><td>${STATUS[o.status]?.label || o.status}</td><td>${PAY_LABELS[o.forma_pagamento] || "—"}</td><td>${fmtMoeda(valorReal(o))}</td><td>${Number(o.cashback_usado||0) > 0 ? fmtMoeda(o.cashback_usado) : "—"}</td><td>${getTurno(o.criado_em)}</td><td>${fmtData(o.criado_em)}</td></tr>`).join("")}
    </table>`;
    exportPDF("Relatório de Vendas", kpiHTML + turnoHTML + tabelaHTML, "relatorio_vendas");
  };

  const exportFinanceiroExcel = () => {
    const headers = ["Código", "Pagamento", "Subtotal", "Taxa Entrega", "Desconto", "Cashback Usado", "Total", "Turno", "Data"];
    const rows = vendidos.map(o => [
      o.codigo, PAY_LABELS[o.forma_pagamento] || o.forma_pagamento,
      Number(o.subtotal || 0).toFixed(2), Number(o.taxa_entrega || 0).toFixed(2), Number(o.desconto || 0).toFixed(2),
      Number(o.cashback_usado || 0).toFixed(2), valorReal(o).toFixed(2), getTurno(o.criado_em), fmtData(o.criado_em)
    ]);
    rows.push(["", "TOTAIS", totalSub.toFixed(2), totalTaxa.toFixed(2), totalDesc.toFixed(2), cashUsado.toFixed(2), totalFat.toFixed(2), "", ""]);
    exportExcel(headers, rows, "relatorio_financeiro");
  };

  const exportFinanceiroPDF = () => {
    const kpiHTML = `<div class="kpi-row">
      <div class="kpi"><div class="kpi-val green">${fmtMoeda(totalFat)}</div><div class="kpi-label">Faturamento</div></div>
      <div class="kpi"><div class="kpi-val blue">${fmtMoeda(totalSub)}</div><div class="kpi-label">Subtotal</div></div>
      <div class="kpi"><div class="kpi-val orange">${fmtMoeda(totalTaxa)}</div><div class="kpi-label">Taxas Entrega</div></div>
      <div class="kpi"><div class="kpi-val">${fmtMoeda(ticketMedio)}</div><div class="kpi-label">Ticket Médio</div></div>
    </div>`;
    const pagHTML = `<h2>Por Forma de Pagamento</h2><table>
      <tr><th>Forma</th><th>Pedidos</th><th>Faturamento</th><th>%</th></tr>
      ${porPag.map(p => `<tr><td>${p.tipo}</td><td>${p.ped}</td><td>${fmtMoeda(p.fat)}</td><td>${totalFat > 0 ? (p.fat / totalFat * 100).toFixed(1) : 0}%</td></tr>`).join("")}
    </table>`;
    const detHTML = `<h2>Detalhamento (${vendidos.length} pedidos)</h2><table>
      <tr><th>Código</th><th>Pagamento</th><th>Subtotal</th><th>Taxa</th><th>Total</th><th>Data</th></tr>
      ${vendidos.map(o => `<tr><td>#${o.codigo}</td><td>${PAY_LABELS[o.forma_pagamento] || "—"}</td><td>${fmtMoeda(o.subtotal)}</td><td>${fmtMoeda(o.taxa_entrega)}</td><td><strong>${fmtMoeda(valorReal(o))}</strong></td><td>${fmtData(o.criado_em)}</td></tr>`).join("")}
    </table>`;
    exportPDF("Relatório Financeiro", kpiHTML + pagHTML + detHTML, "relatorio_financeiro");
  };

  const exportEntregadoresExcel = () => {
    const headers = ["Entregador", "ID", "Pedidos", "Entregas", "Faturamento", "Taxa Entrega", "Média", "Cancelados", "Manhã", "Tarde", "Noite"];
    const rows = porEnt.map(e => [
      e.nome, e.id, e.ped, e.entregas, e.fat.toFixed(2), e.taxa.toFixed(2),
      e.entregas > 0 ? (e.fat / e.entregas).toFixed(2) : "0.00", e.canc,
      e.turno.Manha, e.turno.Tarde, e.turno.Noite
    ]);
    exportExcel(headers, rows, "relatorio_entregadores");
  };

  const exportEntregadoresPDF = () => {
    const kpiHTML = `<div class="kpi-row">
      <div class="kpi"><div class="kpi-val green">${totalEntregas}</div><div class="kpi-label">Total Entregas</div></div>
      <div class="kpi"><div class="kpi-val blue">${porEnt.length}</div><div class="kpi-label">Entregadores</div></div>
      <div class="kpi"><div class="kpi-val orange">${fmtMoeda(totalTaxaEnt)}</div><div class="kpi-label">Taxas</div></div>
    </div>`;
    const rankHTML = `<h2>Ranking</h2><table>
      <tr><th>#</th><th>Entregador</th><th>Entregas</th><th>Faturamento</th><th>Taxa</th></tr>
      ${porEnt.map((e, i) => `<tr><td>${i + 1}</td><td>${e.nome}</td><td>${e.entregas}</td><td>${fmtMoeda(e.fat)}</td><td>${fmtMoeda(e.taxa)}</td></tr>`).join("")}
    </table>`;
    exportPDF("Relatório de Entregadores", kpiHTML + rankHTML, "relatorio_entregadores");
  };

  /* ════════════════════════════════════════════════
     SETOR: VISÃO GERAL (DASHBOARD)
     ════════════════════════════════════════════════ */
  const SetorGeral = () => (
    <>
      <FilterBar accentColor="#3B82F6" />

      {/* KPIs principais */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 10, marginBottom: 16 }}>
        <KpiCard label="Total Pedidos" value={filtrados.length} color="#3B82F6" delay={0} />
        <KpiCard label="Vendidos" value={vendidos.length} color={C.green} delay={.04} />
        <KpiCard label="Faturamento" value={fmt(totalFat)} color="#F59E0B" delay={.08} />
        <KpiCard label="Ticket Médio" value={fmt(ticketMedio)} color="#8B5CF6" delay={.12} />
        <KpiCard label="Cancelados" value={`${cancelados.length} (${taxaCanc}%)`} color={C.red} delay={.16} />
        <KpiCard label="Cashback Usado" value={fmt(cashUsado)} color="#8B5CF6" delay={.2} />
      </div>

      {/* Turnos */}
      <TurnoGrid getData={(k) => [
        { value: turnoData[k]?.ped || 0, label: "pedidos" },
        { value: fmt(turnoData[k]?.fat || 0), label: "faturamento", color: C.green },
        { value: turnoData[k]?.canc || 0, label: "cancelados", color: turnoData[k]?.canc > 0 ? C.red : C.textMuted },
      ]} />

      {/* Gráficos */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12, marginBottom: 16 }}>
        <Card>
          <SectionTitle>{periodo === "hoje" || periodo === "ontem" ? "Pedidos por Hora" : "Pedidos por Dia"}</SectionTitle>
          <ResponsiveContainer width="100%" height={180}>
            {periodo === "hoje" || periodo === "ontem" ? (
              <BarChart data={porHora}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="h" tick={{ fill: C.textMuted, fontSize: 9 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: C.textMuted, fontSize: 9 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="v" name="Pedidos" radius={[4, 4, 0, 0]} fill="#3B82F6" />
              </BarChart>
            ) : (
              <AreaChart data={porDia}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="dia" tick={{ fill: C.textMuted, fontSize: 9 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: C.textMuted, fontSize: 9 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="ped" name="Pedidos" stroke="#3B82F6" fill="#3B82F630" strokeWidth={2} />
              </AreaChart>
            )}
          </ResponsiveContainer>
        </Card>

        <Card>
          <SectionTitle>Por Status</SectionTitle>
          {statusData.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <PieChart width={150} height={150}>
                <Pie data={statusData} cx={72} cy={72} innerRadius={40} outerRadius={65} paddingAngle={3} dataKey="value">
                  {statusData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
              </PieChart>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center", marginTop: 8 }}>
                {statusData.map((s, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: s.color }} />
                    <span style={{ fontSize: 9, color: C.textSoft }}>{s.name} ({s.value})</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <EmptyState />}
        </Card>
      </div>

      {/* Resumo pagamentos + Produtos top 5 */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
        <Card>
          <SectionTitle>Formas de Pagamento</SectionTitle>
          {porPag.length === 0 ? <EmptyState text="Sem dados" /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {porPag.map((p, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 3, background: PAY_COLORS[p.key] || "#888", flexShrink: 0 }} />
                  <span style={{ fontSize: 12, fontWeight: 600, flex: 1, color: C.text }}>{p.tipo}</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: C.green }}>{fmt(p.fat)}</span>
                  <div style={{ width: 60, height: 6, borderRadius: 3, background: C.border, overflow: "hidden" }}>
                    <div style={{ width: `${totalFat > 0 ? (p.fat / totalFat * 100) : 0}%`, height: "100%", borderRadius: 3, background: PAY_COLORS[p.key] || "#888" }} />
                  </div>
                  <span style={{ fontSize: 10, color: C.textMuted, minWidth: 36, textAlign: "right" }}>{totalFat > 0 ? (p.fat / totalFat * 100).toFixed(0) : 0}%</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <SectionTitle>Top Produtos</SectionTitle>
          {porProduto.length === 0 ? <EmptyState text="Sem dados de produtos" /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {porProduto.slice(0, 8).map((p, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: i < 3 ? "#F59E0B" : C.textMuted, minWidth: 18 }}>{i + 1}.</span>
                  <span style={{ fontSize: 12, fontWeight: 600, flex: 1, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.nome}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#06B6D4" }}>{p.qtd}x</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: C.green }}>{fmt(p.fat)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Cashback resumo */}
      {(cashUsado > 0 || cashGerado > 0) && (
        <Card style={{ marginBottom: 16 }}>
          <SectionTitle>Cashback — Resumo do Período</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            <MiniStat label="Cashback Usado (desconto cliente)" value={fmt(cashUsado)} color="#8B5CF6" sub={`${pedidosCashback.filter(o => Number(o.cashback_usado || 0) > 0).length} pedidos`} />
            <MiniStat label="Cashback Gerado (a pagar)" value={fmt(cashGerado)} color={C.orange} sub="Creditado nos clientes" />
            <MiniStat label="Saldo (Gerado - Usado)" value={fmt(cashGerado - cashUsado)} color={cashGerado > cashUsado ? C.red : C.green} sub={cashGerado > cashUsado ? "Mais gerado que usado" : "Mais usado que gerado"} />
          </div>
        </Card>
      )}

      {/* Entregadores resumo */}
      {porEnt.length > 0 && (
        <Card>
          <SectionTitle>Top Entregadores</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 }}>
            {porEnt.slice(0, 6).map((e, i) => (
              <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: 12, borderRadius: 10, background: C.bg, border: `1px solid ${C.border}` }}>
                <Medal pos={i + 1} />
                <div style={{ flex: 1, overflow: "hidden" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.nome}</div>
                  <div style={{ fontSize: 10, color: C.textMuted }}>{e.entregas} entregas</div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 800, color: C.green }}>{fmt(e.fat)}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </>
  );

  /* ════════════════════════════════════════════════
     SETOR: VENDAS
     ════════════════════════════════════════════════ */
  const SetorVendas = () => {
    const lista = vendidos.sort((a, b) => (b.criado_em || "").localeCompare(a.criado_em || ""));
    const listaPag = lista.slice((pag - 1) * POR_PAGINA, pag * POR_PAGINA);
    return (
      <>
        <FilterBar accentColor={C.green} onExportExcel={exportVendasExcel} onExportPDF={exportVendasPDF} />

        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 16 }}>
          <KpiCard label="Vendidos" value={vendidos.length} color={C.green} delay={0} />
          <KpiCard label="Faturamento" value={fmt(totalFat)} color="#3B82F6" delay={.05} />
          <KpiCard label="Ticket Médio" value={fmt(ticketMedio)} color="#F59E0B" delay={.1} />
          <KpiCard label="Taxa Entrega" value={fmt(totalTaxa)} color="#8B5CF6" delay={.15} />
          <KpiCard label="Cashback Usado" value={fmt(cashUsado)} color="#8B5CF6" delay={.2} />
        </div>

        <TurnoGrid getData={(k) => [
          { value: turnoData[k]?.ped || 0, label: "pedidos" },
          { value: fmt(turnoData[k]?.fat || 0), label: "faturamento", color: C.green },
        ]} />

        {/* Gráfico por dia */}
        {porDia.length > 1 && (
          <Card style={{ marginBottom: 16 }}>
            <SectionTitle>Vendas por Dia</SectionTitle>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={porDia}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="dia" tick={{ fill: C.textMuted, fontSize: 9 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: C.textMuted, fontSize: 9 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="fat" name="Faturamento" stroke={C.green} fill={C.green + "30"} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Pagamentos breakdown */}
        {porPag.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 8, marginBottom: 16 }}>
            {porPag.map((p, i) => (
              <div key={i} style={{ background: C.card, borderRadius: 10, padding: 12, border: `1px solid ${C.border}`, textAlign: "center" }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: PAY_COLORS[p.key] || "#888" }}>{fmt(p.fat)}</div>
                <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>{p.tipo} ({p.ped})</div>
              </div>
            ))}
          </div>
        )}

        {/* Tabela */}
        <Card>
          {lista.length === 0 ? <EmptyState text="Nenhum pedido vendido no período" /> : (
            <>
              <SectionTitle>Pedidos Vendidos ({lista.length})</SectionTitle>
              <DataTable headers={["Código", "Status", "Pagamento", "Total", "Cashback", "Turno", "Data"]}>
                {listaPag.map(o => <OrderRow key={o.id} o={o} showCashback />)}
              </DataTable>
              <Pagination total={lista.length} perPage={POR_PAGINA} current={pag} onChange={setPag} C={C} />
            </>
          )}
        </Card>
      </>
    );
  };

  /* ════════════════════════════════════════════════
     SETOR: FINANCEIRO
     ════════════════════════════════════════════════ */
  const SetorFinanceiro = () => {
    return (
      <>
        <FilterBar accentColor="#F59E0B" onExportExcel={exportFinanceiroExcel} onExportPDF={exportFinanceiroPDF} />

        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 16 }}>
          <KpiCard label="Faturamento" value={fmt(totalFat)} color={C.green} delay={0} />
          <KpiCard label="Subtotal Produtos" value={fmt(totalSub)} color="#3B82F6" delay={.05} />
          <KpiCard label="Taxas Entrega" value={fmt(totalTaxa)} color="#F59E0B" delay={.1} />
          <KpiCard label="Descontos" value={fmt(totalDesc)} color={C.red} delay={.15} />
          <KpiCard label="Ticket Médio" value={fmt(ticketMedio)} color="#8B5CF6" delay={.2} />
        </div>

        <TurnoGrid getData={(k) => [
          { value: fmt(turnoData[k]?.fat || 0), label: "faturamento", color: C.green },
          { value: turnoData[k]?.ped || 0, label: "pedidos" },
          { value: fmt(turnoData[k]?.taxa || 0), label: "taxa", color: "#F59E0B" },
        ]} />

        {/* Gráficos */}
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12, marginBottom: 16 }}>
          <Card>
            <SectionTitle>Faturamento por Dia</SectionTitle>
            {fatDia.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={fatDia}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                  <XAxis dataKey="dia" tick={{ fill: C.textMuted, fontSize: 9 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: C.textMuted, fontSize: 9 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Area type="monotone" dataKey="valor" name="Faturamento" stroke={C.green} fill={C.green + "30"} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <EmptyState text="Dados insuficientes" />}
          </Card>

          <Card>
            <SectionTitle>Formas de Pagamento</SectionTitle>
            {pgPie.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <PieChart width={150} height={150}>
                  <Pie data={pgPie} cx={72} cy={72} innerRadius={40} outerRadius={65} paddingAngle={3} dataKey="value">
                    {pgPie.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                </PieChart>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center", marginTop: 8 }}>
                  {pgPie.map((p, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: p.color }} />
                      <span style={{ fontSize: 9, color: C.textSoft }}>{p.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <EmptyState />}
          </Card>
        </div>

        {/* Tabela detalhada pagamento */}
        <Card>
          <SectionTitle>Detalhamento por Forma de Pagamento</SectionTitle>
          {porPag.length === 0 ? <EmptyState /> : (
            <DataTable headers={["Forma", "Pedidos", "Faturamento", "Ticket Médio", "%"]}>
              {porPag.map((p, i) => (
                <tr key={i}>
                  <td style={{ padding: "10px 12px", fontWeight: 700, fontSize: 13 }}>
                    <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, background: PAY_COLORS[p.key] || "#888", marginRight: 8 }} />
                    {p.tipo}
                  </td>
                  <td style={{ padding: "10px 12px", fontWeight: 700 }}>{p.ped}</td>
                  <td style={{ padding: "10px 12px", fontWeight: 800, color: C.green }}>{fmt(p.fat)}</td>
                  <td style={{ padding: "10px 12px", color: C.textSoft }}>{fmt(p.ped > 0 ? p.fat / p.ped : 0)}</td>
                  <td style={{ padding: "10px 12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ flex: 1, height: 6, borderRadius: 3, background: C.border }}>
                        <div style={{ width: `${totalFat > 0 ? (p.fat / totalFat * 100) : 0}%`, height: "100%", borderRadius: 3, background: PAY_COLORS[p.key] || "#888" }} />
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: C.textSoft, minWidth: 36 }}>{totalFat > 0 ? (p.fat / totalFat * 100).toFixed(1) : 0}%</span>
                    </div>
                  </td>
                </tr>
              ))}
              <tr style={{ borderTop: `2px solid ${C.border}` }}>
                <td style={{ padding: "10px 12px", fontWeight: 800 }}>TOTAL</td>
                <td style={{ padding: "10px 12px", fontWeight: 800 }}>{vendidos.length}</td>
                <td style={{ padding: "10px 12px", fontWeight: 800, color: C.green, fontSize: 15 }}>{fmt(totalFat)}</td>
                <td style={{ padding: "10px 12px", fontWeight: 700, color: C.textSoft }}>{fmt(ticketMedio)}</td>
                <td style={{ padding: "10px 12px", fontWeight: 800 }}>100%</td>
              </tr>
            </DataTable>
          )}
        </Card>
      </>
    );
  };

  /* ════════════════════════════════════════════════
     SETOR: ENTREGADORES
     ════════════════════════════════════════════════ */
  const SetorEntregadores = () => {

    /* Detalhe de um entregador */
    if (selectedEnt) {
      const ent = porEnt.find(e => String(e.id) === String(selectedEnt));
      const entOrders = filtrados.filter(o => String(o.entregador_id) === String(selectedEnt)).sort((a, b) => (b.criado_em || "").localeCompare(a.criado_em || ""));
      const entDone = entOrders.filter(o => DONE_STATUS.includes(o.status));
      const entCanc = entOrders.filter(o => o.status === "cancelado");
      const entFat = entDone.reduce((s, o) => s + Number(o.total || 0), 0);
      const entTaxa = entDone.reduce((s, o) => s + Number(o.taxa_entrega || 0), 0);

      const entDiaMap = {};
      entDone.forEach(o => { const d = o.criado_em?.slice(0, 10); if (d) entDiaMap[d] = (entDiaMap[d] || 0) + 1; });
      const entDiaChart = Object.entries(entDiaMap).sort(([a], [b]) => a.localeCompare(b)).map(([d, v]) => ({ dia: d.slice(5), entregas: v }));

      const entPag = entOrders.slice((pag - 1) * POR_PAGINA, pag * POR_PAGINA);

      return (
        <>
          <FilterBar accentColor="#F97316" />
          <button onClick={() => { setSelectedEnt(null); setPag(1); }} style={{ marginBottom: 16, padding: "8px 16px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.text, cursor: "pointer", fontSize: 12, fontWeight: 700 }}>← Voltar para todos</button>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: `${C.green}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 800, color: C.green }}>{(ent?.nome || "?")[0]}</div>
            <div>
              <h2 style={{ fontFamily: "'Plus Jakarta Sans'", fontWeight: 800, fontSize: 22 }}>{ent?.nome || `Entregador #${selectedEnt}`}</h2>
              <span style={{ fontSize: 12, color: C.textMuted }}>ID: {selectedEnt}</span>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 16 }}>
            <KpiCard label="Pedidos" value={entOrders.length} color="#3B82F6" delay={0} />
            <KpiCard label="Entregas" value={entDone.length} color={C.green} delay={.05} />
            <KpiCard label="Faturamento" value={fmt(entFat)} color="#F59E0B" delay={.1} />
            <KpiCard label="Taxa Entrega" value={fmt(entTaxa)} color="#8B5CF6" delay={.15} />
            <KpiCard label="Cancelados" value={entCanc.length} color={C.red} delay={.2} />
          </div>
          <TurnoGrid getData={(k) => [
            { value: ent?.turno?.[k] || 0, label: "entregas" },
          ]} />
          {entDiaChart.length > 1 && (
            <Card style={{ marginBottom: 16 }}>
              <SectionTitle>Entregas por Dia</SectionTitle>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={entDiaChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                  <XAxis dataKey="dia" tick={{ fill: C.textMuted, fontSize: 9 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: C.textMuted, fontSize: 9 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="entregas" radius={[4, 4, 0, 0]} fill={C.green} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}
          <Card>
            <SectionTitle>Todos os Pedidos ({entOrders.length})</SectionTitle>
            {entOrders.length === 0 ? <EmptyState text="Nenhum pedido" /> : (
              <>
                {/* ─── Barra de ações de pagamento ─── */}
                {entOrders.some(o => Number(o.taxa_paga||0)===0 && Number(o.taxa_entrega||0)>0) && (
                  <div style={{ background: "#F59E0B12", border: `1px solid #F59E0B40`, borderRadius: 10, padding: 12, marginBottom: 12, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#F59E0B" }}>💰 PAGAMENTO DE TAXAS</div>
                      <div style={{ fontSize: 12, color: C.textSoft, marginTop: 2 }}>
                        Pendente: <b style={{ color: "#F59E0B" }}>{fmt(ent?.taxaPendente || 0)}</b> · 
                        Já pago: <b style={{ color: "#16A34A" }}>{fmt(ent?.taxaPaga || 0)}</b>
                      </div>
                    </div>
                    {Object.keys(selectedPed).length > 0 && (
                      <button onClick={() => {
                          const ids = Object.keys(selectedPed).map(Number);
                          const total = entOrders.filter(o => selectedPed[o.id]).reduce((s,o)=>s+Number(o.taxa_entrega||0),0);
                          setConfirmModal({ tipo: "selecionados", ids, total, entNome: ent?.nome || "" });
                        }} disabled={savingTaxa}
                        style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: "#16A34A", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
                        ✓ Marcar {Object.keys(selectedPed).length} selecionado(s)
                      </button>
                    )}
                    <button onClick={() => setConfirmModal({ tipo: "todos_ent", entId: selectedEnt, entNome: ent?.nome || "", total: ent?.taxaPendente || 0 })} disabled={savingTaxa}
                      style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: C.orange || "#F59E0B", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
                      💰 Pagar TODAS as pendentes do período
                    </button>
                  </div>
                )}

                <DataTable headers={["", "Código", "Status", "Pagamento", "Total", "Taxa", "Pago?", "Turno", "Data"]}>
                  {entPag.map(o => <OrderRow key={o.id} o={o} showTaxa showCheckbox />)}
                </DataTable>
                <Pagination total={entOrders.length} perPage={POR_PAGINA} current={pag} onChange={setPag} C={C} />
              </>
            )}
          </Card>
        </>
      );
    }

    return (
      <>
        <FilterBar accentColor="#F97316" onExportExcel={exportEntregadoresExcel} onExportPDF={exportEntregadoresPDF} />

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
          <KpiCard label="Total Entregas" value={totalEntregas} color={C.green} delay={0} />
          <KpiCard label="Entregadores Ativos" value={porEnt.length} color="#3B82F6" delay={.06} />
          <KpiCard label="Taxas de Entrega" value={fmt(totalTaxaEnt)} color="#F59E0B" delay={.12} />
          <KpiCard label="Média/Entregador" value={mediaEnt.toFixed(1)} color="#8B5CF6" delay={.18} />
        </div>

        <TurnoGrid getData={(k) => [
          { value: turnoEnt[k] || 0, label: "entregas" },
          { value: totalEntregas > 0 ? ((turnoEnt[k] / totalEntregas) * 100).toFixed(0) + "%" : "0%", label: "do total", color: C.textMuted },
        ]} />

        {/* Ranking chart */}
        {porEnt.length > 0 && (
          <Card style={{ marginBottom: 16 }}>
            <SectionTitle>Ranking de Entregas</SectionTitle>
            <ResponsiveContainer width="100%" height={Math.max(180, Math.min(porEnt.length, 10) * 36)}>
              <BarChart data={porEnt.slice(0, 10).map(e => ({ nome: e.nome.length > 15 ? e.nome.slice(0, 15) + "..." : e.nome, entregas: e.entregas }))} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis type="number" tick={{ fill: C.textMuted, fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="nome" tick={{ fill: C.textSoft, fontSize: 10 }} axisLine={false} tickLine={false} width={120} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="entregas" radius={[0, 6, 6, 0]} fill="#F97316" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Cards entregadores clicáveis */}
        <SectionTitle>Entregadores ({porEnt.length})</SectionTitle>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10, marginBottom: 16 }}>
          {porEnt.map((e, i) => (
            <div key={e.id} onClick={() => { setSelectedEnt(e.id); setPag(1); }}
              style={{ background: C.card, borderRadius: 14, padding: 16, border: `1px solid ${C.border}`, position: "relative", cursor: "pointer", transition: "all .2s" }}
              onMouseOver={ev => ev.currentTarget.style.transform = "translateY(-2px)"} onMouseOut={ev => ev.currentTarget.style.transform = ""}>
              <div style={{ position: "absolute", top: 10, right: 12 }}><Medal pos={i + 1} /></div>
              <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 8 }}>{e.nome}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <div>
                  <div style={{ fontSize: 9, color: C.textMuted }}>Entregas</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{e.entregas}</div>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: C.textMuted }}>Faturamento</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.green }}>{fmt(e.fat)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: C.textMuted }}>Taxa Entrega</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#F59E0B" }}>{fmt(e.taxa)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: C.textMuted }}>Média</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.textSoft }}>{fmt(e.entregas > 0 ? e.fat / e.entregas : 0)}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                {TURNOS.map(t => (
                  <div key={t.key} style={{ flex: 1, textAlign: "center", padding: "4px 0", borderRadius: 6, background: `${t.color}12`, border: `1px solid ${t.color}25` }}>
                    <div style={{ fontSize: 8, color: C.textMuted }}>{t.icon}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: t.color }}>{e.turno[t.key]}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8, fontSize: 10, color: C.textMuted, textAlign: "center", fontWeight: 600 }}>Clique para detalhes →</div>
            </div>
          ))}
        </div>

        {/* Valores a pagar */}
        {porEnt.filter(e => e.taxa > 0).length > 0 && (
          <Card>
            <SectionTitle>Valores a Pagar por Entregador</SectionTitle>
            <DataTable headers={["#", "Entregador", "Entregas", "Já Pago", "A Pagar", "Ação"]}>
              {porEnt.filter(e => e.taxa > 0).map((e, i) => (
                <tr key={e.id}>
                  <td style={{ padding: "10px 12px", textAlign: "center" }}><Medal pos={i + 1} /></td>
                  <td style={{ padding: "10px 12px", fontWeight: 700, fontSize: 13 }}>{e.nome}</td>
                  <td style={{ padding: "10px 12px", fontWeight: 700 }}>{e.entregas}</td>
                  <td style={{ padding: "10px 12px", fontWeight: 700, color: "#16A34A" }}>{fmt(e.taxaPaga)}</td>
                  <td style={{ padding: "10px 12px", fontWeight: 800, fontSize: 15, color: e.taxaPendente > 0 ? "#F59E0B" : C.textMuted }}>{fmt(e.taxaPendente)}</td>
                  <td style={{ padding: "10px 12px" }}>
                    {e.taxaPendente > 0 ? (
                      <button onClick={() => setConfirmModal({ tipo: "todos_ent", entId: e.id, entNome: e.nome, total: e.taxaPendente })}
                        disabled={savingTaxa}
                        style={{ padding: "6px 12px", fontSize: 11, fontWeight: 700, border: "none", background: "#16A34A", color: "#fff", borderRadius: 6, cursor: "pointer", opacity: savingTaxa ? 0.5 : 1 }}>
                        💰 Pagar tudo
                      </button>
                    ) : (
                      <span style={{ fontSize: 10, color: "#16A34A", fontWeight: 700 }}>✓ Quitado</span>
                    )}
                  </td>
                </tr>
              ))}
              <tr style={{ borderTop: `2px solid ${C.border}` }}>
                <td style={{ padding: "10px 12px" }} />
                <td style={{ padding: "10px 12px", fontWeight: 800 }}>TOTAL</td>
                <td style={{ padding: "10px 12px", fontWeight: 800 }}>{totalEntregas}</td>
                <td style={{ padding: "10px 12px", fontWeight: 800, fontSize: 14, color: "#16A34A" }}>{fmt(porEnt.reduce((s,e)=>s+(e.taxaPaga||0),0))}</td>
                <td style={{ padding: "10px 12px", fontWeight: 800, fontSize: 16, color: "#F59E0B" }}>{fmt(porEnt.reduce((s,e)=>s+(e.taxaPendente||0),0))}</td>
                <td />
              </tr>
            </DataTable>
          </Card>
        )}
      </>
    );
  };

  /* ════════════════════════════════════════════════
     SETOR: PRODUTOS
     ════════════════════════════════════════════════ */
  const SetorProdutos = () => {
    const totalQtd = porProduto.reduce((s, p) => s + p.qtd, 0);
    const totalFatProd = porProduto.reduce((s, p) => s + p.fat, 0);
    const listaPag = porProduto.slice((pag - 1) * POR_PAGINA, pag * POR_PAGINA);

    return (
      <>
        <FilterBar accentColor="#06B6D4" />

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
          <KpiCard label="Produtos Únicos" value={porProduto.length} color="#06B6D4" delay={0} />
          <KpiCard label="Itens Vendidos" value={totalQtd} color={C.green} delay={.06} />
          <KpiCard label="Faturamento" value={fmt(totalFatProd)} color="#F59E0B" delay={.12} />
          <KpiCard label="Média/Produto" value={fmt(porProduto.length > 0 ? totalFatProd / porProduto.length : 0)} color="#8B5CF6" delay={.18} />
        </div>

        {/* Top 10 chart */}
        {porProduto.length > 0 && (
          <Card style={{ marginBottom: 16 }}>
            <SectionTitle>Top 10 Produtos Mais Vendidos</SectionTitle>
            <ResponsiveContainer width="100%" height={Math.max(180, Math.min(porProduto.length, 10) * 36)}>
              <BarChart data={porProduto.slice(0, 10).map(p => ({ nome: p.nome.length > 20 ? p.nome.slice(0, 20) + "..." : p.nome, qtd: p.qtd }))} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis type="number" tick={{ fill: C.textMuted, fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="nome" tick={{ fill: C.textSoft, fontSize: 10 }} axisLine={false} tickLine={false} width={160} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="qtd" name="Quantidade" radius={[0, 6, 6, 0]} fill="#06B6D4" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Tabela completa */}
        <Card>
          {porProduto.length === 0 ? <EmptyState text="Nenhum produto vendido no período" /> : (
            <>
              <SectionTitle>Todos os Produtos ({porProduto.length})</SectionTitle>
              <DataTable headers={["#", "Produto", "Quantidade", "Faturamento", "Média Unit.", "% Vendas"]}>
                {listaPag.map((p, i) => (
                  <tr key={i}>
                    <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: 700, color: (pag - 1) * POR_PAGINA + i < 3 ? "#F59E0B" : C.textMuted }}>{(pag - 1) * POR_PAGINA + i + 1}</td>
                    <td style={{ padding: "10px 12px", fontWeight: 700, fontSize: 13 }}>{p.nome}</td>
                    <td style={{ padding: "10px 12px", fontWeight: 800, color: "#06B6D4" }}>{p.qtd}</td>
                    <td style={{ padding: "10px 12px", fontWeight: 800, color: C.green }}>{fmt(p.fat)}</td>
                    <td style={{ padding: "10px 12px", color: C.textSoft }}>{fmt(p.qtd > 0 ? p.fat / p.qtd : 0)}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ flex: 1, height: 6, borderRadius: 3, background: C.border }}>
                          <div style={{ width: `${totalQtd > 0 ? (p.qtd / totalQtd * 100) : 0}%`, height: "100%", borderRadius: 3, background: "#06B6D4" }} />
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, minWidth: 36 }}>{totalQtd > 0 ? (p.qtd / totalQtd * 100).toFixed(1) : 0}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </DataTable>
              <Pagination total={porProduto.length} perPage={POR_PAGINA} current={pag} onChange={setPag} C={C} />
            </>
          )}
        </Card>
      </>
    );
  };

  /* ════════════════════════════════════════════════
     SETOR: CASHBACK
     ════════════════════════════════════════════════ */
  const SetorCashback = () => {
    const listaPag = pedidosCashback.slice((pag - 1) * POR_PAGINA, pag * POR_PAGINA);

    return (
      <>
        <FilterBar accentColor="#8B5CF6" />

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
          <KpiCard label="Cashback Usado" value={fmt(cashUsado)} color="#8B5CF6" delay={0} />
          <KpiCard label="Cashback Gerado" value={fmt(cashGerado)} color="#F59E0B" delay={.06} />
          <KpiCard label="Pedidos com Cashback" value={pedidosCashback.length} color="#3B82F6" delay={.12} />
          <KpiCard label="Saldo (Gerado - Usado)" value={fmt(cashGerado - cashUsado)} color={cashGerado > cashUsado ? "#EF4444" : "#22C55E"} delay={.18} />
        </div>

        {/* Explicação */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div style={{ padding: 16, background: "#8B5CF608", borderRadius: 14, border: "1px solid #8B5CF625" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#8B5CF6", marginBottom: 8 }}>Cashback Recebido (Usado pelos clientes)</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#8B5CF6" }}>{fmt(cashUsado)}</div>
            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6 }}>Valor que os clientes usaram como desconto nos pedidos. Este valor já foi abatido do total recebido pela loja.</div>
          </div>
          <div style={{ padding: 16, background: "#F59E0B08", borderRadius: 14, border: "1px solid #F59E0B25" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#F59E0B", marginBottom: 8 }}>Cashback a Pagar (Gerado para clientes)</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#F59E0B" }}>{fmt(cashGerado)}</div>
            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6 }}>Valor creditado como cashback nas contas dos clientes. Este é o custo de fidelização da sua loja.</div>
          </div>
        </div>

        {/* Gráfico por dia */}
        {cashDia.length > 1 && (
          <Card style={{ marginBottom: 16 }}>
            <SectionTitle>Cashback por Dia</SectionTitle>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={cashDia}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="dia" tick={{ fill: C.textMuted, fontSize: 9 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: C.textMuted, fontSize: 9 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="usado" name="Usado" radius={[4, 4, 0, 0]} fill="#8B5CF6" />
                <Bar dataKey="gerado" name="Gerado" radius={[4, 4, 0, 0]} fill="#F59E0B" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Tabela de pedidos com cashback */}
        <Card>
          {pedidosCashback.length === 0 ? <EmptyState text="Nenhum pedido com cashback no período" /> : (
            <>
              <SectionTitle>Pedidos com Cashback ({pedidosCashback.length})</SectionTitle>
              <DataTable headers={["Código", "Status", "Pagamento", "Total", "Cashback Usado", "Cashback Gerado", "Data"]}>
                {listaPag.map(o => (
                  <tr key={o.id}>
                    <td style={{ padding: "10px 12px", fontWeight: 700, fontSize: 12 }}>#{o.codigo}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: STATUS[o.status]?.color, background: STATUS[o.status]?.bg, padding: "3px 8px", borderRadius: 6 }}>{STATUS[o.status]?.label || o.status}</span>
                    </td>
                    <td style={{ padding: "10px 12px", fontSize: 11, color: C.textSoft }}>{PAY_LABELS[o.forma_pagamento] || "—"}</td>
                    <td style={{ padding: "10px 12px", fontWeight: 800, color: C.green }}>{fmt(valorReal(o))}</td>
                    <td style={{ padding: "10px 12px", fontWeight: 800, color: "#8B5CF6" }}>{Number(o.cashback_usado || 0) > 0 ? fmt(o.cashback_usado) : "—"}</td>
                    <td style={{ padding: "10px 12px", fontWeight: 800, color: "#F59E0B" }}>{Number(o.cashback_gerado || 0) > 0 ? fmt(o.cashback_gerado) : "—"}</td>
                    <td style={{ padding: "10px 12px", fontSize: 11, color: C.textMuted }}>{fmtData(o.criado_em)}</td>
                  </tr>
                ))}
              </DataTable>
              <Pagination total={pedidosCashback.length} perPage={POR_PAGINA} current={pag} onChange={setPag} C={C} />
            </>
          )}
        </Card>
      </>
    );
  };

  /* ════════════════════════════════════════════════
     SETOR: TAXA MIXUP (taxa de serviço cobrada do cliente)
     ════════════════════════════════════════════════ */
  const SetorTaxaMixUp = () => {
    const listaPag = pedidosTaxaMixup.slice((pag - 1) * POR_PAGINA, pag * POR_PAGINA);
    const valorMedio = pedidosTaxaMixup.length > 0 ? taxaMixupTotal / pedidosTaxaMixup.length : 0;

    return (
      <>
        <FilterBar accentColor="#10B981" />

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 16 }}>
          <KpiCard label="Taxa MixUp Acumulada" value={fmt(taxaMixupTotal)} color="#10B981" delay={0} />
          <KpiCard label="Pedidos no Período" value={pedidosTaxaMixup.length} color="#3B82F6" delay={.06} />
          <KpiCard label="Valor Médio por Pedido" value={fmt(valorMedio)} color="#8B5CF6" delay={.12} />
        </div>

        {/* Explicação + ação de pagamento */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div style={{ padding: 16, background: "#10B98108", borderRadius: 14, border: "1px solid #10B98125" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#10B981", marginBottom: 8 }}>💵 O que é essa taxa?</div>
            <div style={{ fontSize: 12, color: C.textSoft, lineHeight: 1.6 }}>
              É uma taxa de serviço da plataforma MixUp cobrada do <strong>cliente</strong> em cada pedido. Como o pagamento vai direto para você (loja), este valor precisa ser <strong>repassado para a MixUp</strong> periodicamente.
            </div>
          </div>
          <div style={{ padding: 16, background: "#10B98108", borderRadius: 14, border: "1px solid #10B98125" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#10B981", marginBottom: 8 }}>💳 Total a repassar para a MixUp</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#10B981" }}>{fmt(taxaMixupTotal)}</div>
            <button
              onClick={() => alert("Em breve: gerar Pix Copia e Cola para pagamento à MixUp.\n\nValor a pagar: " + fmt(taxaMixupTotal))}
              disabled={taxaMixupTotal <= 0}
              style={{
                marginTop: 12, padding: "10px 16px", borderRadius: 10, border: "none",
                background: taxaMixupTotal > 0 ? "#10B981" : C.border,
                color: "#fff", fontWeight: 700, fontSize: 13,
                cursor: taxaMixupTotal > 0 ? "pointer" : "not-allowed",
                width: "100%",
              }}
            >
              💸 Gerar Pix para pagar à MixUp
            </button>
          </div>
        </div>

        {/* Gráfico por dia */}
        {taxaMixupDia.length > 1 && (
          <Card style={{ marginBottom: 16 }}>
            <SectionTitle>Taxa MixUp por Dia</SectionTitle>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={taxaMixupDia}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="dia" tick={{ fill: C.textMuted, fontSize: 9 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: C.textMuted, fontSize: 9 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="valor" name="Taxa MixUp" radius={[4, 4, 0, 0]} fill="#10B981" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Tabela de pedidos com taxa de serviço */}
        <Card>
          {pedidosTaxaMixup.length === 0 ? <EmptyState text="Nenhum pedido com taxa de serviço no período" /> : (
            <>
              <SectionTitle>Pedidos com Taxa de Serviço ({pedidosTaxaMixup.length})</SectionTitle>
              <DataTable headers={["Código", "Status", "Total", "Taxa Serviço", "Data"]}>
                {listaPag.map(o => (
                  <tr key={o.id}>
                    <td style={{ padding: "10px 12px", fontWeight: 700, fontSize: 12 }}>#{o.codigo}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: STATUS[o.status]?.color, background: STATUS[o.status]?.bg, padding: "3px 8px", borderRadius: 6 }}>{STATUS[o.status]?.label || o.status}</span>
                    </td>
                    <td style={{ padding: "10px 12px", fontWeight: 800, color: C.green }}>{fmt(valorReal(o))}</td>
                    <td style={{ padding: "10px 12px", fontWeight: 800, color: "#10B981" }}>{fmt(o.taxa_servico)}</td>
                    <td style={{ padding: "10px 12px", fontSize: 11, color: C.textMuted }}>{fmtData(o.criado_em)}</td>
                  </tr>
                ))}
              </DataTable>
              <Pagination total={pedidosTaxaMixup.length} perPage={POR_PAGINA} current={pag} onChange={setPag} C={C} />
            </>
          )}
        </Card>
      </>
    );
  };

  /* ════════════════════════════════════════════════
     SETOR: CANCELAMENTOS
     ════════════════════════════════════════════════ */
  const SetorCancelamentos = () => {
    const perdaTotal = cancelados.reduce((s, o) => s + Number(o.total || 0), 0);
    const listaPag = cancelados.sort((a, b) => (b.criado_em || "").localeCompare(a.criado_em || "")).slice((pag - 1) * POR_PAGINA, pag * POR_PAGINA);

    return (
      <>
        <FilterBar accentColor="#EF4444" />

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
          <KpiCard label="Cancelados" value={cancelados.length} color="#EF4444" delay={0} />
          <KpiCard label="Perda Estimada" value={fmt(perdaTotal)} color="#F59E0B" delay={.06} />
          <KpiCard label="Taxa Cancelamento" value={taxaCanc + "%"} color={parseFloat(taxaCanc) > 5 ? "#EF4444" : "#22C55E"} delay={.12} />
          <KpiCard label="Média por Canc." value={fmt(cancelados.length > 0 ? perdaTotal / cancelados.length : 0)} color="#3B82F6" delay={.18} />
        </div>

        {/* Indicador visual */}
        <div style={{ padding: 12, borderRadius: 10, marginBottom: 16,
          background: parseFloat(taxaCanc) > 10 ? "#EF444412" : parseFloat(taxaCanc) > 5 ? "#F59E0B12" : "#22C55E12",
          border: `1px solid ${parseFloat(taxaCanc) > 10 ? "#EF444430" : parseFloat(taxaCanc) > 5 ? "#F59E0B30" : "#22C55E30"}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 20 }}>{parseFloat(taxaCanc) > 10 ? "🔴" : parseFloat(taxaCanc) > 5 ? "🟡" : "🟢"}</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{parseFloat(taxaCanc) > 10 ? "Taxa alta — acima de 10%" : parseFloat(taxaCanc) > 5 ? "Taxa moderada — entre 5% e 10%" : "Taxa saudável — abaixo de 5%"}</div>
              <div style={{ fontSize: 11, color: C.textMuted }}>{cancelados.length} de {filtrados.length} pedidos cancelados</div>
            </div>
          </div>
        </div>

        {/* Turnos */}
        <TurnoGrid getData={(k) => {
          const cancT = turnoData[k]?.canc || 0;
          const pedT = turnoData[k]?.ped || 0;
          return [
            { value: cancT, label: "cancelados", color: cancT > 0 ? "#EF4444" : C.textMuted },
            { value: pedT > 0 ? ((cancT / pedT) * 100).toFixed(1) + "%" : "0%", label: `de ${pedT}`, color: C.textMuted },
          ];
        }} />

        {/* Motivos */}
        {motivosCanc.length > 0 && (
          <Card style={{ marginBottom: 16 }}>
            <SectionTitle>Motivos de Cancelamento</SectionTitle>
            <DataTable headers={["Motivo", "Quantidade", "% do Total"]}>
              {motivosCanc.map((m, i) => (
                <tr key={i}>
                  <td style={{ padding: "10px 12px", fontSize: 12, fontWeight: 600 }}>{m.motivo}</td>
                  <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 800, color: "#EF4444" }}>{m.qtd}</td>
                  <td style={{ padding: "10px 12px", fontSize: 12, color: C.textMuted }}>{cancelados.length > 0 ? (m.qtd / cancelados.length * 100).toFixed(1) : 0}%</td>
                </tr>
              ))}
            </DataTable>
          </Card>
        )}

        {/* Tabela */}
        <Card>
          {cancelados.length === 0 ? <EmptyState text="Nenhum cancelamento no período" /> : (
            <>
              <SectionTitle>Pedidos Cancelados ({cancelados.length})</SectionTitle>
              <DataTable headers={["Código", "Status", "Pagamento", "Total", "Turno", "Data"]}>
                {listaPag.map(o => <OrderRow key={o.id} o={o} />)}
              </DataTable>
              <Pagination total={cancelados.length} perPage={POR_PAGINA} current={pag} onChange={setPag} C={C} />
            </>
          )}
        </Card>
      </>
    );
  };

  /* ════════════════════════════════════════════════
     RENDER PRINCIPAL
     ════════════════════════════════════════════════ */
  const setorAtual = SETORES.find(s => s.key === setor);

  return (
    <div style={{ animation: "fadeUp .4s ease-out" }}>
      {/* Header */}
      <div style={{ marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <h1 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: 28 }}>Relatórios</h1>
          <p style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>Análise completa do seu negócio</p>
        </div>
      </div>

      {/* Navegação por setores */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20, padding: 4, background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, flexWrap: "wrap" }}>
        {SETORES.map(s => {
          const active = setor === s.key;
          return (
            <button key={s.key} onClick={() => { setSetor(s.key); setPag(1); }}
              style={{
                flex: "1 1 auto", minWidth: 100, padding: "10px 14px", borderRadius: 10, border: active ? `2px solid ${s.color}` : "2px solid transparent",
                background: active ? s.color + "15" : "transparent",
                color: active ? s.color : C.textSoft, fontWeight: 700, fontSize: 12,
                cursor: "pointer", transition: "all .2s", fontFamily: "inherit",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}>
              <span style={{ fontSize: 14 }}>{s.icon}</span>
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Conteúdo do setor ativo */}
      <div key={setor} style={{ animation: "fadeUp .3s ease-out" }}>
        {setor === "geral" && SetorGeral()}
        {setor === "vendas" && SetorVendas()}
        {setor === "financeiro" && SetorFinanceiro()}
        {setor === "entregadores" && SetorEntregadores()}
        {setor === "produtos" && SetorProdutos()}
        {setor === "cashback" && SetorCashback()}
        {setor === "taxa_mixup" && SetorTaxaMixUp()}
        {setor === "cancelamentos" && SetorCancelamentos()}
      </div>

      {/* ─── MODAL CONFIRMAÇÃO DE PAGAMENTO DE TAXA ─── */}
      {confirmModal && (
        <div onClick={() => !savingTaxa && setConfirmModal(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: C.card, borderRadius: 16, padding: 24, width: "100%", maxWidth: 460, border: `1px solid ${C.border}` }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: C.text }}>💰 Confirmar pagamento</h3>
            <div style={{ marginTop: 14, padding: 14, background: C.bg, borderRadius: 10, border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 12, color: C.textMuted }}>Entregador</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>{confirmModal.entNome || "?"}</div>
              <div style={{ fontSize: 12, color: C.textMuted }}>Valor total a pagar</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: "#16A34A" }}>{fmt(confirmModal.total || 0)}</div>
              {confirmModal.tipo === "selecionados" && (
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6 }}>{(confirmModal.ids||[]).length} pedido(s) selecionado(s)</div>
              )}
              {confirmModal.tipo === "todos_ent" && (
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6 }}>
                  Todas as taxas pendentes do entregador no período selecionado
                </div>
              )}
            </div>

            <div style={{ marginTop: 14 }}>
              <label style={{ fontSize: 12, color: C.textMuted, fontWeight: 700 }}>Observação (opcional)</label>
              <input id="modal-obs-taxa" type="text" placeholder="Ex: pago em dinheiro, PIX, etc"
                style={{ width: "100%", marginTop: 6, padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontSize: 13 }} />
            </div>

            <div style={{ marginTop: 18, padding: 10, background: "#F59E0B12", borderRadius: 8, fontSize: 11, color: C.textSoft, border: `1px solid #F59E0B40` }}>
              ⚠️ Esta ação registrará o pagamento com seu nome (<b>{lojistaNome}</b>) e a data/hora atual. Use o botão de estornar caso precise reverter.
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
              <button onClick={() => setConfirmModal(null)} disabled={savingTaxa}
                style={{ flex: 1, padding: "10px 16px", borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", color: C.text, cursor: "pointer", fontSize: 13, fontWeight: 700 }}>
                Cancelar
              </button>
              <button onClick={() => {
                  const obs = document.getElementById("modal-obs-taxa")?.value || "";
                  if (confirmModal.tipo === "selecionados") marcarTaxasPagas(confirmModal.ids, true, obs);
                  else if (confirmModal.tipo === "todos_ent") marcarTodasEntregador(confirmModal.entId, confirmModal.entNome, obs);
                }} disabled={savingTaxa}
                style={{ flex: 2, padding: "10px 16px", borderRadius: 8, border: "none", background: "#16A34A", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 800, opacity: savingTaxa ? 0.6 : 1 }}>
                {savingTaxa ? "Processando..." : "✓ Confirmar pagamento"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default Relatorios;
